import { EventEmitter } from "events";
import { debounce } from "lodash";
import { notEmpty } from "@memento/library/type-witchcraft";
import config from "src/config";

let gptPromise: Promise<googletag.Googletag> | null = null;

/**
 * Get the loaded/ready googletag object from the window.
 * If the gpt script tag has not already been loaded, append the tag
 * to head and load it now.
 */
export const getGoogletag: () => Promise<googletag.Googletag> = () => {
  if (!gptPromise) {
    gptPromise = new Promise<googletag.Googletag>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `${document.location.protocol}//securepubads.g.doubleclick.net/tag/js/gpt.js`;
      script.async = true;
      script.type = "text/javascript";
      script.onerror = (event) => {
        reject(new Error(`Failed to load ${(event as any).target.src}`));
      };
      script.onload = () => initialize(resolve);
      document.querySelector("head")?.appendChild(script);
    });
  }

  return gptPromise;
};

type Command = (googletag: googletag.Googletag) => void;

/** Add a command to the googletag command queue. */
export const pushGoogletagCommand = (command: Command) => {
  return getGoogletag()
    .then((googletag) => {
      googletag.cmd.push(() => {
        command(googletag);
      });
      return googletag;
    })
    .catch((err) => {
      console.log(err);
    });
};

type ManagedSlot = {
  id: string;
  sizes: googletag.GeneralSize[];
  loaded: boolean;
  shouldRefresh: () => boolean;
};

const slots: { [key: string]: ManagedSlot } = {};

type RegisterSlotParams = {
  /** ID of the div which will contain the ad unit */
  id: string;

  /** Name of the ad unit in DFP not including network id */
  adUnit: string;

  /** Ad unit creative sizes to use in this slot */
  sizes: googletag.SingleSizeArray[];

  /** Responsive ads size mappings array */
  sizeMappings: ads.SizeMappings;

  /** Callback function if the unit should refresh after initial load */
  shouldRefresh: () => boolean;

  /** If the slot should not be fetched immediately */
  skipInitialRefresh: boolean;
};

/** The full GPT AdUnitPath including DFP network ID */
const adUnitPath = (adUnit: string) => `/${config.ads.dfpNetworkId}/${adUnit}`;

/** Define the slot in GPT and track in ad manager */
export const registerSlot = ({
  id,
  adUnit,
  sizes,
  sizeMappings,
  shouldRefresh,
  skipInitialRefresh,
}: RegisterSlotParams) => {
  if (!id) {
    return;
  }

  return pushGoogletagCommand((googletag) => {
    const firstSize = sizes[0];
    if (!firstSize) {
      return;
    }

    const slot = googletag
      .defineSlot(adUnitPath(adUnit), firstSize, id)
      .addService(googletag.pubads())
      .setTargeting("pos", adUnit);

    if (sizes.length > 1) {
      // Restrict the sizeMapping to sizes specified for the slot
      const mapping = sizeMappings
        .reduce((sizeBuilder, [viewport, viewportSizes]) => {
          const allowedSizes = viewportSizes.filter(([w, h]) => {
            return sizes.find(([sw, sh]) => sw === w && sh === h);
          });
          return sizeBuilder.addSize(viewport, allowedSizes);
        }, googletag.sizeMapping())
        .build();

      slot.defineSizeMapping(mapping);
    }

    slots[id] = { id, sizes, shouldRefresh, loaded: false };
  })
    .then((googletag) => {
      if (googletag) {
        googletag.cmd.push(() => googletag.display(id));
      }
    })
    .then(() => {
      if (!skipInitialRefresh) {
        refresh(id);
      }
    });
};

const getSlotFromGpt = (googletag: googletag.Googletag, id: string) => {
  const [slot] = googletag
    .pubads()
    .getSlots()
    .filter((s) => s.getSlotElementId() === id);

  return slot;
};

/** Destroy the ad slot in GPT and stop tracking in ad manager */
export const unregisterSlot = (id: string) => {
  if (!id) {
    return;
  }

  getGoogletag()
    .then((googletag) => {
      if (googletag.pubadsReady) {
        const slot = getSlotFromGpt(googletag, id);

        if (slot) {
          googletag.destroySlots([slot]);
        }
      }
      delete slots[id];
    })
    .catch((err) => {
      console.log(err);
    });
};

let pendingRefresh: string[] = [];

/** Refetch ads for the given slots ids or all slots if no slots given. */
export const refresh = (...ids: string[]) => {
  if (ids.length > 0) {
    pendingRefresh = [...pendingRefresh, ...ids];
  } else {
    pendingRefresh = Object.keys(slots);
  }

  doRefresh();
};

const doRefresh = debounce(() => {
  getGoogletag()
    .then((googletag) => {
      const lastRefresh = pendingRefresh;
      pendingRefresh = [];

      const gptSlots = lastRefresh
        .filter((id) => {
          const slot = slots[id];

          if (slot) {
            const { loaded, shouldRefresh } = slot;
            return loaded === false || shouldRefresh();
          } else {
            return false;
          }
        })
        .map((id) => getSlotFromGpt(googletag, id))
        .filter(notEmpty);

      lastRefresh.forEach((id) => {
        const slot = slots[id];

        if (slot) {
          slot.loaded = true;
        }
      });

      googletag.cmd.push(() => {
        googletag.pubads().refresh(gptSlots);
      });
    })
    .catch((err) => {
      console.log(err);
    });
}, 200);

export const emitter: ads.events.EventEmitter = new EventEmitter();

type OnReady = (resolve: googletag.Googletag) => void;

const initialize = (resolve: OnReady) => {
  if (!window.googletag?.apiReady) {
    setTimeout(() => initialize(resolve), 100);
    return;
  }

  const pubads = window.googletag.pubads();

  const getDecorators: (slot: googletag.Slot) => ads.events.SlotIdentifier = (
    slot
  ) => ({
    slotId: slot.getSlotElementId(),
  });

  pubads.addEventListener("slotRenderEnded", (event) => {
    emitter.emit("slotRenderEnded", { ...getDecorators(event.slot), ...event });
  });

  pubads.addEventListener("slotOnload", (event) => {
    emitter.emit("slotOnload", { ...getDecorators(event.slot), ...event });
  });

  pubads.addEventListener("slotRequested", (event) => {
    emitter.emit("slotRequested", { ...getDecorators(event.slot), ...event });
  });

  pubads.addEventListener("slotResponseReceived", (event) => {
    emitter.emit("slotResponseReceived", {
      ...getDecorators(event.slot),
      ...event,
    });
  });

  pubads.addEventListener("slotVisibilityChanged", (event) => {
    emitter.emit("slotVisibilityChanged", {
      ...getDecorators(event.slot),
      ...event,
    });
  });

  pubads.addEventListener("impressionViewable", (event) => {
    emitter.emit("impressionViewable", {
      ...getDecorators(event.slot),
      ...event,
    });
  });

  pubads.disableInitialLoad();
  googletag.enableServices();
  resolve(window.googletag);
};
